import { useEffect, useRef, useState } from 'react';
import { deviceToday, similarOpenLeads } from '@wr/shared';
import { Dialog, formatDateTime } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import type { LocalRecord } from '../../offline/store.js';
import { useSignedIn } from '../../session.js';
import { fullName, type SheetLead } from '../people/data.js';
import {
  IsThereAProject,
  ProjectBlock,
  focusFirst,
  leadOps,
  projectErrors,
  projectFrom,
  type ProjectDraft,
  type ProjectErrors,
} from './Project.js';
import { useStages } from './stages.js';
import { useAskEvent } from '../capture/AskEvent.js';

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/**
 * The lead of a contact who already exists (decision of Nov 2, 2026): + Lead
 * on the contact sheet, Add lead once the person is chosen, and the seller's
 * return from Hand the tablet (`ask`: Is there a project? first). The person
 * is not typed again: block 2 directly, prefilled from the contact, then
 * one Save. What the contact did not have yet is saved on it too; the lead
 * keeps its own copy, linked to the contact (primary). No second form opens:
 * what is missing goes to To do.
 */
export function StartLead({
  contact,
  leads = [],
  eventCode,
  ask = false,
  onCancel,
  onCreated,
}: {
  contact: LocalRecord;
  /** The contact's leads: a similar open one is flagged first (Nov 3, 2026). */
  leads?: readonly SheetLead[];
  eventCode: string | null;
  ask?: boolean;
  onCancel(): void;
  onCreated(leadId: string): void;
}) {
  const { user, can } = useSignedIn();
  const { save, store, currentEventId } = useOffline();
  const askEvent = useAskEvent();
  const stages = useStages();
  const [project, setProject] = useState(!ask);
  const [answers, setAnswers] = useState<ProjectDraft>(() => projectFrom(contact));
  const [errors, setErrors] = useState<ProjectErrors>({});
  const [summary, setSummary] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // A similar open lead of this contact: shown once, never blocking (Nov 3, 2026).
  const [similar, setSimilar] = useState<SheetLead[] | null>(null);
  const [anyway, setAnyway] = useState(false);
  const warnRef = useRef<HTMLDivElement>(null);
  // Shown where the seller looks: the window scrolls to it.
  useEffect(() => {
    if (similar) warnRef.current?.scrollIntoView?.({ block: 'center' });
  }, [similar]);
  const rootRef = useRef<HTMLDivElement>(null);
  const name = fullName(contact) || str(contact.email) || 'this person';
  const own = !contact.created_by || contact.created_by === user.id;
  const canChangeContact = can('contacts.update.all') || (own && can('contacts.update.own'));

  async function create(force = false) {
    if (saving) return;
    if (!project) return onCancel();
    const errs = projectErrors(answers);
    setErrors(errs ?? {});
    if (errs) {
      setSummary('A lead needs the four answers.');
      return focusFirst(rootRef.current, errs);
    }
    if (!anyway && !force) {
      const found = similarOpenLeads(
        leads,
        {
          company: str(contact.company) || null,
          applications: answers.applications,
          eventId: str(contact.event_id) || (await currentEventId()),
        },
        deviceToday(),
      );
      if (found.length > 0) return setSimilar(found);
    }
    setSaving(true);
    try {
      // The contact's event, else this device's (asked during a show, Nov 3, 2026).
      const eventId = str(contact.event_id) || (await askEvent.eventForCapture());
      if (eventId === undefined) return setSaving(false);
      const code =
        eventId === contact.event_id
          ? eventCode
          : ((await store?.getRecord('event', eventId ?? ''))?.event_code as string | undefined);
      const { leadId, ops } = leadOps({
        person: {
          id: contact.id,
          company: str(contact.company) || null,
          country: str(contact.country) || null,
          event_id: eventId || null,
          had: contact,
          canChange: canChangeContact,
        },
        draft: answers,
        eventId: eventId || null,
        eventCode: code ?? null,
        ownerId: user.id,
        qualify: can('leads.qualify'),
        stages,
      });
      await save(...ops);
      onCreated(leadId);
    } catch {
      setSummary('This device could not save the lead. Nothing was lost: try again.');
      setSaving(false);
    }
  }

  return (
    <Dialog
      title={ask ? name : 'New lead'}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            {ask ? 'Close' : 'Keep as a contact'}
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={saving}
            onClick={() => void create()}
          >
            Save
          </button>
        </>
      }
    >
      <div ref={rootRef} className="dlg-form">
        <p style={{ margin: 0 }}>
          <b>{name}</b>
          {str(contact.company) ? `, ${str(contact.company)}` : ''}
          {str(contact.email) ? (
            <>
              <br />
              <span className="note">{str(contact.email)}</span>
            </>
          ) : null}
        </p>
        {ask && (
          <IsThereAProject
            value={project}
            onChange={(yes) => {
              setProject(yes);
              setErrors({});
              setSummary(null);
            }}
          />
        )}
        {project && (
          <ProjectBlock
            value={answers}
            onChange={(v) => {
              setAnswers(v);
              setErrors({});
              setSummary(null);
            }}
            errors={errors}
            personName={str(contact.first_name)}
          />
        )}
        {similar && !anyway && (
          <div ref={warnRef} className="notice warn" role="alert">
            <b>This contact already has a similar open lead</b>
            {similar.map((l) => (
              <span key={l.id} className="note" style={{ display: 'block' }}>
                {l.deal_name || l.company_name || 'Lead'}
                {l.created_at ? `, created ${formatDateTime(l.created_at)}` : ''}
              </span>
            ))}
            <div className="actions">
              <button type="button" className="btn sm" onClick={() => onCreated(similar[0]!.id)}>
                Open the existing lead
              </button>
              <button
                type="button"
                className="btn sm primary"
                onClick={() => {
                  setAnyway(true);
                  setSimilar(null);
                  void create(true);
                }}
              >
                Create a new lead anyway
              </button>
            </div>
          </div>
        )}
        <p className="err" role="alert">
          {summary}
        </p>
      </div>
      {askEvent.dialog}
    </Dialog>
  );
}
